import { PhotoInterface } from '../../interfaces/PhotoInterface';
import { DexieCompletionCache, DexiePhoto } from './implementationModel/dexieObjects';
import { db } from './db';
import { Photo } from '../../interfaces/objects';
import { v4 as uuid } from 'uuid';
import { TaskDexieImplementation } from './taskDexieImplementation';
import { _changeLog, _task } from '../ImplementationFactory';
import { ChangeType } from '../../interfaces/changeLoggerInterface';
import { graphql } from '../../../../api/apiUtils';
import { EnvironmentConfig, getFlag, getNumber } from '../../../../environmentUtils';
import { makeImageFilesizeSafe } from '../../../../../features/checklist/photos/imageUtils';
import { sendErrorEmail } from '../../../../../features/error/errorApi';
import { PhotoTag } from '../../../../../constants/constants';
import { logFriendlyObject } from '@otuvy/common';

const TIME_TO_KEEP_CACHE_IN_MS = getNumber(EnvironmentConfig.TIME_TO_KEEP_CACHE_IN_MS, 1000 * 60 * 60 * 24 * 7);

/**
 * Captured once when this module loads (≈ app start). Photo-add markers created during the current session
 * have a capturedAt after this, so startup recovery only ever acts on markers left over from a PREVIOUS
 * session - never one whose background resize is still in flight right now.
 */
const SESSION_START_MS = Date.now();

export class PhotoDexieImplementation implements PhotoInterface {
  private async getRawPhotoById(photoId: string): Promise<DexiePhoto> {
    const matchingPhotos = await db().photo.where({ photoId }).toArray();

    if (!matchingPhotos || matchingPhotos.length === 0) {
      console.warn(`No photos matching ID ${photoId}`);
      return {
        photoId,
        data: '',
      };
    }
    if (matchingPhotos.length > 1) {
      console.error('Multiple photos matching id ' + photoId, matchingPhotos);
      throw new Error('Multiple photos matching id ' + photoId);
    }

    return matchingPhotos[0];
  }

  private castDexieToPhoto(dexiePhoto: DexiePhoto): Photo {
    const { cacheTime, ...rest } = dexiePhoto;
    return {
      ...rest,
    };
  }

  private castPhotoToDexie(photo: Photo): DexiePhoto {
    return {
      ...photo,
    };
  }

  async getById(photoId: string): Promise<Photo> {
    const photo: DexiePhoto = await this.getRawPhotoById(photoId);
    return this.castDexieToPhoto(photo);
  }

  async getPhoto(photoId: string): Promise<Photo> {
    return this.getById(photoId);
  }

  async getThumbnail(photoId: string): Promise<Photo> {
    const matchingThumbNails = await db().thumbnail.where({ photoId }).toArray();

    if (!matchingThumbNails || matchingThumbNails.length === 0) {
      console.warn(`No thumb nails matching ID ${photoId}`);
      return {
        photoId: '',
        data: '',
      };
    }
    if (matchingThumbNails.length > 1) {
      throw new Error('Multiple thumb nails matching id ' + photoId);
    }

    return this.castDexieToPhoto(matchingThumbNails[0]);
  }

  async addCompletionPhotoToTask(taskId: string, photoData: string): Promise<Photo> {
    console.log('addCompletionPhotoToTask - start');
    const newPhotoID = uuid();
    const capturedAt = Date.now();

    // Record the intent to add this photo BEFORE doing anything else, so that if the app is closed at any
    // point before the add-photo change log is queued, startup recovery can finish the job (or report it).
    console.log('addCompletionPhotoToTask - recording pending photo add');
    await db().pendingPhotoAdd.put({ photoId: newPhotoID, taskId, capturedAt });

    console.log('addCompletionPhotoToTask - saving local photo');
    const localPhoto: Photo = {
      photoId: newPhotoID,
      data: photoData,
    };
    await this.savePhoto(localPhoto);

    console.log('addCompletionPhotoToTask - adding photo to task');
    await _task.addCompletionPhotoToTask(taskId, newPhotoID);

    // Resize and queue the add-photo change log in the BACKGROUND so the (CPU-heavy) resize doesn't hold up
    // the saving indicator. The change log is stamped with capturedAt so it still sorts - and is processed
    // server-side - before any tag edit the user makes afterward, even though it may be queued a moment later.
    // The pendingPhotoAdd marker (written above) covers the window until this completes.
    console.log('addCompletionPhotoToTask - initiating background resize and change log queue');
    void this.queueCompletionPhotoAdd(taskId, newPhotoID, photoData, capturedAt);

    console.log('addCompletionPhotoToTask - returning');
    return localPhoto;
  }

  /**
   * Re-runs any photo adds that didn't finish in a previous session (their pendingPhotoAdd marker survived
   * an app close before the background queue completed). For each stale marker: if the local photo is still
   * present, re-queue its add (stamped with the original capture time) and alert; if the photo is gone too,
   * alert that it is unrecoverable. Only markers from before this session are touched, so an add whose
   * resize is still in flight right now is never disturbed. Safe to call at the start of each sync.
   */
  async recoverIncompletePhotoAdds(): Promise<void> {
    const staleMarkers = (await db().pendingPhotoAdd.toArray()).filter(
      (marker) => marker.capturedAt < SESSION_START_MS
    );

    for (const marker of staleMarkers) {
      try {
        const localPhoto = await db().photo.where({ photoId: marker.photoId }).first();
        if (!localPhoto || !localPhoto.data) {
          if (getFlag(EnvironmentConfig.CAPTURE_LOGS)) {
            sendErrorEmail(
              'Completion photo add could not be recovered',
              `photoId: ${marker.photoId}
taskId: ${marker.taskId}
capturedAt: ${marker.capturedAt}
reason: local photo not found on device`
            );
          }
          await db().pendingPhotoAdd.delete(marker.photoId);
          continue;
        }

        const success = await this.queueCompletionPhotoAdd(
          marker.taskId,
          marker.photoId,
          localPhoto.data,
          marker.capturedAt
        );
        if (!success) {
          throw new Error('Failed to queue completion photo add');
        }

        // Alert that photo processing was not completed originally and had to be recovered
        if (getFlag(EnvironmentConfig.CAPTURE_LOGS)) {
          sendErrorEmail(
            'Recovered an incomplete completion photo add',
            `photoId: ${marker.photoId}
taskId: ${marker.taskId}
capturedAt: ${marker.capturedAt}`
          );
        }
      } catch (e) {
        console.error('Error recovering incomplete completion photo add', logFriendlyObject(e));
        if (getFlag(EnvironmentConfig.CAPTURE_LOGS)) {
          sendErrorEmail(
            'Error recovering incomplete completion photo add',
            `photoId: ${marker.photoId}
taskId: ${marker.taskId}
capturedAt: ${marker.capturedAt}`
          );
        }
      }
    }
  }

  /**
   * Resizes the photo, queues its COMPLETION_ADD_PHOTO change log (stamped with capturedAt), then clears the
   * pendingPhotoAdd marker. Intended to run un-awaited so the resize stays off the critical path. If it does
   * not complete (e.g. the app is closed mid-resize), the marker remains and recoverIncompletePhotoAdds
   * re-runs it next session. A duplicate add is harmless: the server upserts photos by id.
   */
  private async queueCompletionPhotoAdd(
    taskId: string,
    photoId: string,
    photoData: string,
    capturedAt: number
  ): Promise<boolean> {
    try {
      const processedImage = await makeImageFilesizeSafe(photoData);
      const serverFriendlyPhoto: Photo = {
        photoId,
        data: processedImage,
      };
      await _changeLog.queueChange({
        changeType: ChangeType.COMPLETION_ADD_PHOTO,
        recordId: taskId,
        payload: JSON.stringify(serverFriendlyPhoto),
        createdOn: capturedAt,
      });
      console.log('queueCompletionPhotoAdd - change log queued');
      await db().pendingPhotoAdd.delete(photoId);
      return true;
    } catch (e) {
      // Leave the marker in place so startup recovery retries. (makeImageFilesizeSafe itself never rejects.)
      console.error('Failed to queue completion photo add', logFriendlyObject(e));
      return false;
    }
  }

  async getCompletionPhotoIdsForTask(taskId: string): Promise<string[]> {
    const matchingCompletion: DexieCompletionCache =
      await new TaskDexieImplementation().getCurrentCompletionForTaskById(taskId);

    return matchingCompletion.completionPhotoIds ? matchingCompletion.completionPhotoIds : [];
  }

  private async savePhoto(photo: Photo) {
    const dexiePhoto: DexiePhoto = this.castPhotoToDexie(photo);
    dexiePhoto.cacheTime = new Date();
    await db().photo.add(dexiePhoto);
    await db().thumbnail.delete(dexiePhoto.photoId); //Once we have the full photo, no longer store the thumbnail
  }

  async removeCompletionPhotoFromTask(taskId: string, photoId: string) {
    await db().photo.delete(photoId);
    await _task.removeCompletionPhotoFromTask(taskId, photoId);

    _changeLog.queueChange({
      changeType: ChangeType.COMPLETION_REMOVE_PHOTO,
      recordId: taskId,
      payload: JSON.stringify({ photoId: photoId }),
    });
  }

  async downloadThumbnail(photoId: string) {
    if (!photoId) {
      console.error('No photoID supplied trying to download Thumbnails');
    }

    const query = `
      query getThumbnailsFromServer($photoIds: [ID!]!) {
        getThumbnails(photoIds: $photoIds) {
          photoId
          data
          tag
        }
      }
    `;

    const variables = {
      photoIds: [photoId],
    };

    const apiResults = await graphql<Photo[]>(query, variables);
    if (!apiResults) return null;

    const thumbnailsWithCacheInfo = await this.withoutPendingTagEdits(
      apiResults.map<DexiePhoto>((thumbNail) => ({
        ...thumbNail,
        cacheTime: new Date(),
      }))
    );

    await db().thumbnail.bulkPut(thumbnailsWithCacheInfo);

    return apiResults[0];
  }

  async updatePhotoAndThumbnail(
    taskId: string,
    photoId: string,
    changes: Partial<DexiePhoto>,
    changeType: ChangeType = ChangeType.COMPLETION_PHOTO_EDIT
  ): Promise<Photo> {
    const photoUpdatedCount = await db().photo.update(photoId, changes);
    const thumbnailUpdatedCount = await db().thumbnail.update(photoId, changes);

    // If neither the photo nor its thumbnail exists locally, we're about to queue a change log (e.g. a
    // tag edit) referencing a photo the device doesn't have - the exact shape of the missing-photo /
    // orphaned-tag failures we've investigated. Surface it for diagnosis; the change is still queued.
    if (photoUpdatedCount === 0 && thumbnailUpdatedCount === 0 && getFlag(EnvironmentConfig.CAPTURE_LOGS)) {
      sendErrorEmail(
        'Photo edit for a photo missing on device',
        `taskId: ${taskId}\nphotoId: ${photoId}\nchangeType: ${changeType}\nchanges: ${JSON.stringify(changes)}`
      );
    }

    const task = await db().task.where({ taskId }).first();
    if (!task) {
      throw new Error('Task not found for taskId ' + taskId);
    }

    const updatedOn = new Date();
    await db().task.update(taskId, { updatedOn });
    await db().list.update(task.listId, { updatedOn });

    await _changeLog.queueChange({
      changeType,
      recordId: photoId,
      payload: JSON.stringify(changes),
    });

    const result = await this.getById(photoId);
    return result;
  }

  async updateTag(taskId: string, photoId: string, tag: PhotoTag | null) {
    return this.updatePhotoAndThumbnail(taskId, photoId, { tag });
  }

  async downloadPhoto(photoId: string) {
    if (!photoId) {
      console.error('No photoID supplied trying to download Photos');
    }

    const query = `
      query getPhotosFromServer($photoIds: [ID!]!) {
        getPhotos(photoIds: $photoIds) {
          photoId
          data
          tag
        }
      }
    `;
    const variables = {
      photoIds: [photoId],
    };
    const apiResults = await graphql<Photo[]>(query, variables);
    if (!apiResults) return null;

    const photosWithCacheInfo: DexiePhoto[] = await this.withoutPendingTagEdits(
      apiResults.map<DexiePhoto>((photo) => ({
        ...photo,
        cacheTime: new Date(),
      }))
    );
    await db().photo.bulkPut(photosWithCacheInfo);

    //delete thumbnails now that we have the full photos (only those we actually wrote)
    const thumbNailReferences: string[] = photosWithCacheInfo.map((photo) => {
      return photo.photoId;
    });
    await db().thumbnail.bulkDelete(thumbNailReferences);

    return apiResults[0];
  }

  /**
   * Drops any downloaded photos that still have a pending (un-synced) tag edit, so writing the server's
   * copy can't clobber a tag the user changed locally but hasn't uploaded yet. We already hold that photo
   * locally (its pending edit updated it), and photo data is immutable, so skipping the write loses
   * nothing; the pending edit re-applies the tag on the server when it syncs.
   */
  private async withoutPendingTagEdits(downloaded: DexiePhoto[]): Promise<DexiePhoto[]> {
    const pendingTagPhotoIds = new Set(await _changeLog.getPhotoIdsWithPendingTagChanges());
    if (pendingTagPhotoIds.size === 0) return downloaded;
    return downloaded.filter((photo) => !pendingTagPhotoIds.has(photo.photoId));
  }

  async cleanUpImageCache() {
    //TODO: If the user is offline more than TIME_TO_KEEP_CACHE we will be removing photos that they took and that are not uploaded yet.  Since that data is stored in the change log this should be ok
    const cacheTime = new Date(new Date().getTime() - TIME_TO_KEEP_CACHE_IN_MS);

    await db().thumbnail.where('cacheTime').below(cacheTime).delete();
    await db().photo.where('cacheTime').below(cacheTime).delete();
  }
}
